import { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HeadphonesOutlinedIcon from "@mui/icons-material/HeadphonesOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NumbersIcon from "@mui/icons-material/Numbers";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FormatLineSpacingIcon from "@mui/icons-material/FormatLineSpacing";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Drawer from "../../components/ui/Drawer";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useListWorksQuery } from "../../api/workApi";
import { SoftButton, cls } from "../hatim/soft/SoftKit";
import {
  ACTIVITY_LABEL_KEYS,
  STEP,
  STEP_COUNT,
  UNIT_HINT_KEYS,
  UNIT_LABEL_KEYS,
  WEEK_DAYS,
  allowedActivities,
  allowedUnits,
  buildWizardValue,
  deriveWizard,
  formatWeekDays,
  nextStep,
  resolveActivity,
  resolveUnit,
  stepError,
  toggleWeekDay,
  unitWord,
} from "./goalRules";

const ACTIVITY_ICONS = {
  okuma: MenuBookOutlinedIcon,
  ezber: PsychologyOutlinedIcon,
  yazma: EditOutlinedIcon,
  dinleme: HeadphonesOutlinedIcon,
  calisma: TimerOutlinedIcon,
};

const UNIT_ICONS = {
  minute: AccessTimeIcon,
  count: NumbersIcon,
  page: DescriptionOutlinedIcon,
  line: FormatLineSpacingIcon,
  tally: AddCircleOutlineIcon,
};

const STEP_KEYS = [
  { title: "gw_step_work", description: "gw_step_work_desc" },
  { title: "gw_step_activity", description: "gw_step_activity_desc" },
  { title: "gw_step_unit", description: "gw_step_unit_desc" },
  { title: "gw_step_days", description: "gw_step_days_desc" },
  { title: "gw_step_amount", description: "gw_step_amount_desc" },
  { title: "gw_step_summary", description: "gw_step_summary_desc" },
];

/** Eser listesinden sihirbazın tuttuğu eser bilgisi. */
function workFromItem(item) {
  return {
    id: item.id,
    name: item.name,
    parentName: item.parentName,
    isCollection: !!item.isCollection,
    totalPage: item.totals?.page ?? null,
    units: item.units,
    activities: item.activities,
  };
}

function initialState(initial = {}) {
  return {
    step: 0,
    label: initial.label || "",
    unit: initial.unit || "count",
    activity: initial.activity || "okuma",
    work: initial.workId ? { id: initial.workId, name: initial.workName || initial.label || "", totalPage: null } : null,
    planDays: initial.planDays ? String(initial.planDays) : "",
    weekDays: Array.isArray(initial.weekDays) ? [...initial.weekDays] : [],
    amount: initial.totalValue
      ? String(initial.totalValue)
      : initial.goalValue
        ? String(initial.goalValue)
        : "",
  };
}

/**
 * Sihirbazın durumu ve geçişleri. Gövde ve alt düğmeler ayrı bileşen:
 * alt düğmeler çekmecenin sabit alt bölgesinde durur.
 *
 * mode "edit": ad ve biçim değişmez (sunucu hedefi ad + biçimle buluyor).
 * mode "add": ad kütüphaneden seçilir; initial verilirse (şablon kalemi
 * düzenlenirken) o değerlerle açılır.
 */
export function useGoalWizard({ open, mode = "add", initial, onSubmit }) {
  const isEdit = mode === "edit";
  const [state, setState] = useState(() => initialState(initial));
  const [error, setError] = useState(null);

  // Arama terimi addan ayrı; içine girilen üst eserler iz olarak tutulur.
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [trail, setTrail] = useState([]);

  // Her açılışta baştan: başlangıç değerleri o anki hedeften.
  useEffect(() => {
    if (!open) return;
    setState(initialState(initial));
    setError(null);
    setTerm("");
    setDebounced("");
    setTrail([]);
    // initial yalnız açılışta okunur; açıkken dışarıdan gelen değişiklik formu sıfırlamasın.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const q = term.trim();
    if (q.length === 1) return undefined; // tek harfte arama yapma
    const timer = setTimeout(() => setDebounced(q), q.length >= 2 ? 300 : 0);
    return () => clearTimeout(timer);
  }, [term]);

  const parent = trail.length ? trail[trail.length - 1] : null;
  const listArgs = debounced.length >= 2 ? { search: debounced } : { parentId: parent?.id ?? null };
  const works = useListWorksQuery(listArgs, { skip: !open || isEdit || state.step !== STEP.WORK });

  // Başlangıçta bağlı eserin biçim, birim ve külliyat bilgisi listeden
  // tamamlanır; yoksa müstakil eser sayılır (mobildeki gibi).
  const initialWorkName = initial?.workId ? initial.workName || initial.label : "";
  const lookup = useListWorksQuery(
    { search: initialWorkName },
    { skip: !open || !initialWorkName || String(initialWorkName).trim().length < 2 }
  );
  useEffect(() => {
    const found = (lookup.data || []).find((w) => String(w.id) === String(initial?.workId));
    if (!found) return;
    setState((s) => (s.work && String(s.work.id) === String(found.id) ? { ...s, work: workFromItem(found) } : s));
  }, [lookup.data, initial?.workId]);

  const { work, activity } = state;
  const activityChoices = useMemo(
    () => (isEdit ? [initial?.activity || "okuma"] : allowedActivities(work?.activities)),
    [isEdit, initial?.activity, work?.activities]
  );
  const unitChoices = useMemo(
    () => allowedUnits(activity, work?.units, work?.isCollection),
    [activity, work?.units, work?.isCollection]
  );

  // Biçim ya da eser değişince uygun olmayan birim önerilene düşer.
  useEffect(() => {
    setState((s) => {
      const next = resolveUnit(s.unit, s.activity, s.work?.units, s.work?.isCollection);
      return next === s.unit ? s : { ...s, unit: next };
    });
  }, [activity, work?.units, work?.isCollection]);

  const set = useCallback((patch) => {
    setError(null);
    setState((s) => ({ ...s, ...patch }));
  }, []);

  const pickWork = (item) => {
    const picked = workFromItem(item);
    setError(null);
    setState((s) => {
      const nextActivity = resolveActivity(s.activity, item.activities);
      return {
        ...s,
        work: picked,
        label: item.name,
        activity: nextActivity,
        unit: resolveUnit(s.unit, nextActivity, item.units, picked.isCollection),
        // Eserin sayfa sayısı, boşsa miktar önerisi olur.
        amount: picked.totalPage && !s.amount ? String(picked.totalPage) : s.amount,
      };
    });
  };

  const clearWork = () => set({ work: null, label: "" });
  const openWork = (item) => {
    setTerm("");
    setDebounced("");
    setTrail((prev) => [...prev, item]);
  };
  const goUp = () => setTrail((prev) => prev.slice(0, -1));

  const counts = { activityCount: activityChoices.length, unitCount: unitChoices.length };
  const wizardState = { ...state, mode };
  const derived = deriveWizard(state);
  const isLast = state.step === STEP_COUNT - 1;

  const next = () => {
    const err = stepError(state.step, wizardState);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    if (!isLast) {
      setState((s) => ({ ...s, step: nextStep(s.step, 1, counts) }));
      return;
    }
    onSubmit(buildWizardValue(state));
  };

  const back = () => {
    setError(null);
    setState((s) => ({ ...s, step: nextStep(s.step, -1, counts) }));
  };

  return {
    mode,
    isEdit,
    state,
    derived,
    error,
    isLast,
    activityChoices,
    unitChoices,
    set,
    next,
    back,
    search: {
      term,
      setTerm,
      trail,
      goUp,
      openWork,
      pickWork,
      clearWork,
      items: works.data || [],
      loading: works.isFetching,
      failed: works.isError,
      loaded: works.isSuccess,
    },
  };
}

function Chip({ selected, onClick, children, icon: Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={[
        "inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm transition-colors",
        selected
          ? "bg-terakkiPrimaryDark text-white dark:bg-white dark:text-terakkiPrimaryDark"
          : `${cls.chip} hover:bg-terakkiBrandMist dark:hover:bg-white/[0.16]`,
      ].join(" ")}
    >
      {Icon ? <Icon sx={{ fontSize: 16 }} aria-hidden /> : null}
      {children}
    </button>
  );
}

function Note({ icon: Icon = InfoOutlinedIcon, children, tone = "muted" }) {
  return (
    <p className={`flex items-start gap-2 text-sm mt-3 ${tone === "danger" ? cls.danger : cls.muted}`}>
      <Icon sx={{ fontSize: 16, mt: "2px" }} aria-hidden />
      <span>{children}</span>
    </p>
  );
}

function NumberField({ label, value, onChange, placeholder, maxLength }) {
  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, "").slice(0, maxLength))}
      placeholder={placeholder}
      size="small"
      fullWidth
      inputProps={{ inputMode: "numeric", pattern: "[0-9]*", "aria-label": label }}
      className={cls.input}
    />
  );
}

function WorkStep({ w, t }) {
  const { state, isEdit, search } = w;
  if (isEdit) {
    return (
      <div className={`flex items-center gap-2 py-2 ${cls.row}`}>
        <FlagOutlinedIcon fontSize="small" className={cls.accent} aria-hidden />
        <span className={`truncate ${cls.title}`}>{state.label}</span>
      </div>
    );
  }
  const { work } = state;
  const q = search.term.trim();
  return (
    <div className="flex flex-col">
      <div className="sticky top-0 z-10 bg-terakkiSurface pb-2 dark:bg-[#123B3D]">
        <TextField
          value={search.term}
          onChange={(e) => search.setTerm(e.target.value.slice(0, 60))}
          placeholder={t("gw_search_placeholder")}
          size="small"
          fullWidth
          inputProps={{ "aria-label": t("gw_search_placeholder") }}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" className={`mr-1 ${cls.muted}`} />,
            endAdornment: search.term ? (
              <button type="button" onClick={() => search.setTerm("")} aria-label={t("gw_search_clear")}>
                <CloseIcon fontSize="small" className={cls.muted} />
              </button>
            ) : null,
          }}
          className={cls.input}
        />
        {work ? (
          <div className={`flex items-center gap-2 mt-2 px-3 py-2 rounded-lg ${cls.chip}`}>
            <CheckIcon sx={{ fontSize: 16 }} aria-hidden />
            <span className="text-sm truncate flex-1">{[work.parentName, work.name].filter(Boolean).join(" › ")}</span>
            <button type="button" onClick={search.clearWork} aria-label={t("gw_clear_work")}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </button>
          </div>
        ) : null}
        {search.trail.length && !q ? (
          <button
            type="button"
            onClick={search.goUp}
            className={`mt-2 flex items-center gap-1.5 text-sm ${cls.accent}`}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} aria-hidden />
            <span className="truncate">{search.trail.map((x) => x.name).join(" › ")}</span>
          </button>
        ) : null}
      </div>

      {search.loading ? (
        <div className="flex justify-center py-8">
          <CircularProgress size={28} />
        </div>
      ) : search.failed ? (
        <Note tone="danger">{t("gw_works_failed")}</Note>
      ) : search.loaded && search.items.length === 0 ? (
        <Note>{t("gw_works_empty")}</Note>
      ) : (
        <ul>
          {search.items.map((item) => {
            const selected = work?.id === item.id;
            return (
              <li key={item.id} className={`flex items-center ${cls.row}`}>
                <button
                  type="button"
                  onClick={() => search.pickWork(item)}
                  aria-pressed={selected}
                  className={`flex-1 min-w-0 flex items-center gap-3 py-2.5 pr-2 text-left ${cls.rowHover}`}
                >
                  {selected ? (
                    <CheckIcon fontSize="small" className={cls.accent} aria-hidden />
                  ) : (
                    <AutoStoriesOutlinedIcon fontSize="small" className={cls.muted} aria-hidden />
                  )}
                  <span className={`flex-1 min-w-0 truncate ${selected ? `font-SemiBold ${cls.accent}` : cls.title}`}>
                    {item.name}
                  </span>
                  {item.totals?.page ? (
                    <span className={`text-xs shrink-0 ${cls.muted}`}>
                      {item.totals.page} {t("gw_word_page")}
                    </span>
                  ) : null}
                </button>
                {item.hasChildren ? (
                  // Alt bölümü olan eser: satır kendisini seçer, ok içine girer.
                  <button
                    type="button"
                    onClick={() => search.openWork(item)}
                    aria-label={`${t("gw_open_work")} ${item.name}`}
                    className={`w-9 h-9 flex items-center justify-center rounded-full ${cls.rowHover} ${cls.accent}`}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function amountLabel({ unit, derived, work }, t) {
  if (!derived.hasPlan) {
    if (unit === "minute") return t("gw_amount_daily_minute");
    if (unit === "line") return t("gw_amount_daily_line");
    if (unit === "page") return t("gw_amount_daily_page");
    return t("gw_amount_daily_count");
  }
  const suffix =
    unit === "line" ? t("gw_amount_total_line") : unit === "page" ? t("gw_amount_total_page") : t("gw_amount_total_count");
  return work?.name ? `${work.name} ${suffix}` : `${t("gw_total")} ${suffix}`;
}

function amountDescription(activity, hasPlan) {
  if (hasPlan) return "gw_step_amount_desc";
  return `gw_amount_desc_${activity in ACTIVITY_LABEL_KEYS ? activity : "okuma"}`;
}

/** Özet satırları: ad, biçim, birim, günler, süre, planlıysa toplam. */
export function summaryRows(state, derived, t) {
  const { work, label, activity, unit, weekDays } = state;
  const rows = [
    { key: "goal", label: t("gw_sum_goal"), value: work ? [work.parentName, work.name].filter(Boolean).join(" › ") : label.trim() },
    { key: "activity", label: t("gw_sum_activity"), value: t(ACTIVITY_LABEL_KEYS[activity] || "gw_activity_okuma") },
    { key: "unit", label: t("gw_sum_unit"), value: t(UNIT_LABEL_KEYS[unit] || "gw_unit_count") },
    { key: "days", label: t("gw_sum_week_days"), value: formatWeekDays(weekDays, t) },
    {
      key: "duration",
      label: t("gw_sum_duration"),
      value: derived.hasDuration
        ? `${derived.plan?.planDays ?? derived.days} ${t("gw_days_lasting")}`
        : t("gw_repeating"),
    },
  ];
  if (derived.hasPlan) {
    rows.push({ key: "total", label: t("gw_total"), value: `${derived.total ?? 0} ${unitWord(unit, t)}` });
  }
  return rows;
}

/** Sihirbazın gövdesi: nokta göstergesi, adım başlığı ve içeriği. */
export function GoalWizardBody({ wizard: w }) {
  const { t } = useScriptMode();
  const { state, derived } = w;
  const { step, unit, activity, weekDays } = state;
  const word = unitWord(unit, t);
  const description = step === STEP.AMOUNT ? amountDescription(activity, derived.hasPlan) : STEP_KEYS[step].description;

  return (
    <div className="px-5 py-4 flex flex-col gap-3">
      <div className="flex items-center gap-1.5" aria-label={`${step + 1} / ${STEP_COUNT}`}>
        {STEP_KEYS.map((s, i) => (
          <span
            key={s.title}
            className={[
              "h-1.5 rounded-full transition-all",
              i === step ? "w-6 bg-terakkiPrimary dark:bg-[#8FC9CA]" : "w-1.5",
              i < step ? "bg-terakkiPrimary/60 dark:bg-[#8FC9CA]/60" : i > step ? cls.track : "",
            ].join(" ")}
          />
        ))}
      </div>

      <div>
        <h3 className={`text-lg font-SemiBold ${cls.heading}`}>{t(STEP_KEYS[step].title)}</h3>
        <p className={`text-sm mt-0.5 ${cls.muted}`}>{t(description)}</p>
      </div>

      {step === STEP.WORK ? <WorkStep w={w} t={t} /> : null}

      {step === STEP.ACTIVITY ? (
        <div className="flex flex-wrap gap-2">
          {w.activityChoices.map((key) => (
            <Chip key={key} selected={activity === key} onClick={() => w.set({ activity: key })} icon={ACTIVITY_ICONS[key]}>
              {t(ACTIVITY_LABEL_KEYS[key])}
            </Chip>
          ))}
        </div>
      ) : null}

      {step === STEP.UNIT ? (
        <ul>
          {w.unitChoices.map((key) => {
            const selected = unit === key;
            const Icon = UNIT_ICONS[key];
            return (
              <li key={key} className={cls.row}>
                <button
                  type="button"
                  onClick={() => w.set({ unit: key })}
                  aria-pressed={selected}
                  className={`w-full flex items-center gap-3 py-3 text-left ${cls.rowHover}`}
                >
                  <Icon fontSize="small" className={selected ? cls.accent : cls.muted} aria-hidden />
                  <span className="flex-1 min-w-0">
                    <span className={`block ${selected ? `font-SemiBold ${cls.accent}` : cls.title}`}>
                      {t(UNIT_LABEL_KEYS[key])}
                    </span>
                    <span className={`block text-xs ${cls.muted}`}>{t(UNIT_HINT_KEYS[key])}</span>
                  </span>
                  {selected ? <CheckIcon fontSize="small" className={cls.accent} aria-hidden /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {step === STEP.DAYS ? (
        <div className="flex flex-col gap-2">
          <NumberField
            label={t("gw_days_field")}
            value={state.planDays}
            onChange={(v) => w.set({ planDays: v })}
            placeholder={t("gw_days_placeholder")}
            maxLength={4}
          />
          <Note>{t(derived.planRequired ? "gw_days_required_note" : "gw_days_note")}</Note>
          <div className={`text-sm mt-3 ${cls.title}`}>{t("gw_sum_week_days")}</div>
          <div className="flex flex-wrap gap-2">
            <Chip selected={weekDays.length === 0} onClick={() => w.set({ weekDays: [] })}>
              {t("gw_every_day")}
            </Chip>
            {WEEK_DAYS.map((d) => (
              <Chip
                key={d.value}
                selected={weekDays.includes(d.value)}
                onClick={() => w.set({ weekDays: toggleWeekDay(weekDays, d.value) })}
              >
                {t(d.key)}
              </Chip>
            ))}
          </div>
        </div>
      ) : null}

      {step === STEP.AMOUNT ? (
        <div className="flex flex-col">
          <NumberField
            label={amountLabel({ unit, derived, work: state.work }, t)}
            value={state.amount}
            onChange={(v) => w.set({ amount: v })}
            placeholder={derived.hasPlan && unit === "page" ? "600" : "10"}
            maxLength={6}
          />
          {derived.hasPlan && derived.days ? (
            <Note>{t("gw_amount_min_note", { days: derived.days, unit: word })}</Note>
          ) : null}
          {derived.dailyTarget ? (
            <Note icon={FlagOutlinedIcon}>
              {derived.hasDuration
                ? `${t("gw_daily_goal")}: ${derived.dailyTarget} · ${derived.plan?.planDays ?? derived.days} ${t("gw_days")}` +
                  (derived.plan?.extraDays ? ` · ${derived.plan.extraDays} ${t("gw_days")} ${derived.plan.extraValue}` : "")
                : `${t("gw_daily_goal")}: ${derived.dailyTarget} · ${t("gw_every_day_lower")}`}
            </Note>
          ) : null}
        </div>
      ) : null}

      {step === STEP.SUMMARY ? (
        <div className="flex flex-col">
          <ul>
            {summaryRows(state, derived, t).map((row) => (
              <li key={row.key} className={`flex items-start justify-between gap-4 py-2.5 ${cls.row}`}>
                <span className={`text-sm shrink-0 ${cls.muted}`}>{row.label}</span>
                <span className={`text-sm text-right ${cls.title}`}>{row.value}</span>
              </li>
            ))}
          </ul>
          <div className={`mt-4 px-4 py-3 rounded-lg ${cls.chip}`}>
            <div className="text-xs">{t("gw_daily_goal")}</div>
            <div className="text-xl">
              {derived.dailyTarget ?? 0} {word}
            </div>
            {derived.plan?.extraDays ? (
              <div className="text-xs mt-0.5">
                {derived.plan.extraDays} {t("gw_days")} {derived.plan.extraValue} {word}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Alt düğmeler: solda geri / sil / iptal, sağda ileri / kaydet. */
export function GoalWizardFooter({ wizard: w, submitting, submitLabel, error, onCancel, onDelete }) {
  const { t } = useScriptMode();
  const first = w.state.step === 0;
  // Adım hatası önce; yoksa çağıranın hatası (ör. listede aynı hedef var).
  const message = w.error ? t(w.error) : error;
  return (
    <div className="flex flex-col gap-2">
      {message ? (
        <p role="alert" className={`text-sm ${cls.danger}`}>
          {message}
        </p>
      ) : null}
      <div className="flex gap-2">
        {!first ? (
          <SoftButton tone="soft" fullWidth onClick={w.back} disabled={submitting}>
            {t("gw_back")}
          </SoftButton>
        ) : onDelete ? (
          <SoftButton tone="danger" fullWidth onClick={onDelete} disabled={submitting}>
            {t("delete")}
          </SoftButton>
        ) : (
          <SoftButton tone="cancel" fullWidth onClick={onCancel} disabled={submitting}>
            {t("cancel")}
          </SoftButton>
        )}
        <SoftButton tone={w.isLast ? "success" : "primary"} fullWidth onClick={w.next} disabled={submitting}>
          {submitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : w.isLast ? (
            submitLabel || t(w.isEdit ? "gw_update" : "gw_save")
          ) : (
            t("gw_next")
          )}
        </SoftButton>
      </div>
    </div>
  );
}

/**
 * Hedef formu: sağdan açılan çekmecede adım adım sihirbaz. Kaydetme işi
 * çağırana ait: onSubmit sihirbazın sonucunu alır (goalRules.toGoalRequest
 * ile istek gövdesine, toQrItem ile QR kalemine çevrilir). Toplu atama gibi
 * kaydetmeden yalnız hedef nesnesi isteyen yerler de aynı onSubmit'i kullanır.
 */
export default function GoalWizardDrawer({
  open,
  mode = "add",
  initial,
  title,
  subtitle,
  submitting = false,
  submitLabel,
  error,
  onSubmit,
  onClose,
  onDelete,
}) {
  const { t } = useScriptMode();
  const wizard = useGoalWizard({ open, mode, initial, onSubmit });
  return (
    <Drawer
      open={open}
      onClose={submitting ? undefined : onClose}
      width="sm"
      title={title || t(mode === "edit" ? "gw_title_edit" : "gw_title_add")}
      subtitle={subtitle}
      titleIcon={<FlagOutlinedIcon fontSize="small" />}
      footer={
        <GoalWizardFooter
          wizard={wizard}
          submitting={submitting}
          submitLabel={submitLabel}
          error={error}
          onCancel={onClose}
          onDelete={onDelete}
        />
      }
    >
      <GoalWizardBody wizard={wizard} />
    </Drawer>
  );
}
